import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Command, CommandGroup, CommandItem, CommandList } from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useState } from 'react';
import { AuditEventCategory, OPT_IN_CATEGORIES } from 'types/entity-audit-trail';
import { t } from 'utils/getTranslations';
import { FilterTriggerIndicator } from 'pages/audit-trail/FilterTriggerIndicator';
import { FILTER_TRIGGER_CLASSES } from 'pages/audit-trail/toolbarStyles';
import { EntityAuditTrailDescriptor } from './entityAuditTrail';
import { categoryLabel } from './entityAuditTrailFormat';

export function AuditCategoryFilter({
    trail,
    selected,
    onChange,
}: {
    trail: EntityAuditTrailDescriptor;
    selected: AuditEventCategory[];
    onChange: (selected: AuditEventCategory[]) => void;
}) {
    const [open, setOpen] = useState(false);

    const toggle = (category: AuditEventCategory) => {
        onChange(
            selected.includes(category) ? selected.filter((value) => value !== category) : [...selected, category]
        );
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger className={FILTER_TRIGGER_CLASSES}>
                {t(`${trail.localeNamespace}.event-type`)}
                <FilterTriggerIndicator count={selected.length} />
            </PopoverTrigger>
            <PopoverContent
                align="start"
                // Opens from the entity history drawer, which stacks above the popover's own tier.
                positionerClassName="z-(--z-dropdown)"
                className="w-[260px] rounded-xl p-1.5"
            >
                <Command>
                    <CommandList>
                        <CommandGroup>
                            {trail.categories.map((category) => (
                                <CommandItem key={category} value={category} onSelect={() => toggle(category)}>
                                    <Checkbox
                                        checked={selected.includes(category)}
                                        tabIndex={-1}
                                        className="pointer-events-none"
                                    />
                                    <span className="flex flex-col">
                                        {categoryLabel(trail.localeNamespace, category)}
                                        {OPT_IN_CATEGORIES.has(category) && (
                                            <span className="text-muted-foreground new:text-caption text-[11.5px]">
                                                {t('audit-trail.opt-in-hint')}
                                            </span>
                                        )}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    <div className="border-grey-100 border-t pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-grey-600 w-full text-[12.5px]"
                            disabled={selected.length === 0}
                            onClick={() => onChange([])}
                        >
                            {t('audit-trail.clear-all')}
                        </Button>
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
